/**
 * useClickOutside — reusable hook that calls `handler` when a click
 * lands outside every element referenced by `refs`.
 *
 * Supports Escape key dismissal when `options.escapeKey` is true (default).
 */

import { type RefObject, useEffect } from 'react';

interface UseClickOutsideOptions {
  /** Also close on Escape key press (default: true) */
  escapeKey?: boolean;
}

export function useClickOutside(
  refs: RefObject<HTMLElement | null>[],
  handler: () => void,
  options: UseClickOutsideOptions = {},
): void {
  const { escapeKey = true } = options;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const isOutside = refs.every((ref) => !ref.current?.contains(target));
      if (isOutside) handler();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handler();
    };

    document.addEventListener('mousedown', handleClick);
    if (escapeKey) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClick);
      if (escapeKey) {
        document.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [refs, handler, escapeKey]);
}
